/**
 * SyncStatus Component
 *
 * Purpose: Visual indicator for offline queue and sync status
 *
 * Features:
 *   - Green dot: All synced
 *   - Spinning arrows: Syncing
 *   - Red warning: Error
 *   - Number badge: Items in queue
 *
 * Usage:
 *   <SyncStatus />
 *
 * Reference: LeltárMester Egyesített Refaktorálási Terv (v2.0)
 */

import React from 'react';
import { useOfflineQueue } from '@/hooks';
import { CheckCircle2, AlertCircle, RefreshCw, Clock } from 'lucide-react';

export const SyncStatus: React.FC = () => {
  const { queueLength, isSyncing, lastError, syncProgress } = useOfflineQueue();

  // Determine status
  const hasError = !!lastError;
  const hasQueue = queueLength > 0;
  const isAllSynced = !isSyncing && !hasError && !hasQueue;

  // Determine icon and color
  const getStatusIcon = () => {
    if (hasError) {
      return <AlertCircle className="text-red-500" size={20} />;
    }

    if (isSyncing) {
      return <RefreshCw className="text-blue-500 animate-spin" size={20} />;
    }

    if (hasQueue) {
      return <Clock className="text-yellow-500" size={20} />;
    }

    return <CheckCircle2 className="text-green-500" size={20} />;
  };

  // Determine status text
  const getStatusText = () => {
    if (hasError) {
      return 'Szinkronizálási hiba';
    }

    if (isSyncing) {
      if (syncProgress) {
        return `Szinkronizálás... (${syncProgress.processed}/${syncProgress.total})`;
      }
      return 'Szinkronizálás...';
    }

    if (hasQueue) {
      return `${queueLength} tétel vár`;
    }

    return 'Minden rendben';
  };

  // Tooltip text
  const getTooltipText = () => {
    if (hasError) {
      return `Hiba: ${lastError}`;
    }

    if (isSyncing && syncProgress) {
      return `Szinkronizálás folyamatban:\n${syncProgress.successful} sikeres, ${syncProgress.failed} sikertelen`;
    }

    if (hasQueue) {
      return `${queueLength} tétel vár szinkronizálásra`;
    }

    return 'Minden adat szinkronizálva';
  };

  return (
    <div
      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 transition-all hover:shadow-md cursor-default"
      title={getTooltipText()}
    >
      {/* Icon */}
      <div className="flex-shrink-0">{getStatusIcon()}</div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {getStatusText()}
        </p>
      </div>

      {/* Badge (if queue has items) */}
      {hasQueue && !isSyncing && (
        <div className="flex-shrink-0">
          <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-yellow-500 rounded-full">
            {queueLength}
          </span>
        </div>
      )}

      {/* Error indicator */}
      {hasError && (
        <div className="flex-shrink-0">
          <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
            !
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * Compact version for navbar
 */
export const SyncStatusCompact: React.FC = () => {
  const { queueLength, isSyncing, lastError } = useOfflineQueue();

  const hasError = !!lastError;
  const hasQueue = queueLength > 0;

  if (!isSyncing && !hasError && !hasQueue) {
    // All synced - show nothing or a tiny green dot
    return (
      <div className="relative" title="Minden szinkronizálva">
        <CheckCircle2 className="text-green-500" size={20} />
      </div>
    );
  }

  return (
    <div className="relative" title={isSyncing ? 'Szinkronizálás...' : `${queueLength} tétel vár`}>
      {/* Icon */}
      {hasError && <AlertCircle className="text-red-500" size={20} />}
      {isSyncing && !hasError && <RefreshCw className="text-blue-500 animate-spin" size={20} />}
      {!isSyncing && !hasError && hasQueue && <Clock className="text-yellow-500" size={20} />}

      {/* Badge */}
      {queueLength > 0 && !isSyncing && (
        <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-yellow-500 rounded-full min-w-[18px]">
          {queueLength}
        </span>
      )}
      {hasError && (
        <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-4 h-4 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
          !
        </span>
      )}
    </div>
  );
};
